/**
 * Plan colours in the renderer (D178, docs/plans/2026-10-concurrent-plans.md
 * section 5): the one place a plan's colour slot becomes something drawn, so
 * the board, the timeline, the office, the team map and Mission Control can
 * never disagree about which colour a plan is.
 *
 * The slot is the host's (`Plan.colorIndex`, picked so running plans differ;
 * `planColorIndex` falls back to a hash of the id for a plan without one).
 * The colours are the `--plan-tone-0` … `--plan-tone-7` tokens in index.css,
 * stepped for light and dark and validated against both design systems.
 *
 * A view that only knows a plan's id (a task's `metadata.planId`, a timeline
 * run's `plan.id`) asks {@link planToneOf}, which reads the slot every
 * `plans-state` the renderer has seen recorded — so a task chip on the board
 * is the same colour as its plan on the Plans view, not a guess from the hash.
 *
 * DOM-free apart from the optional listener, so the tests hold it.
 */

import * as planIdentityModule from '@aioe/shared/coordination/planIdentity';

/**
 * The shared definition, whichever way it was loaded: Vite sees `shared/` as the ES module it is
 * written as; Node's test runner loads it as the root package's CommonJS, whose named exports only
 * show on the module object (`default`) — as `inboxModel.ts` does.
 */
export const planIdentity = ((planIdentityModule as unknown as { default?: typeof planIdentityModule }).default ?? planIdentityModule) as typeof planIdentityModule;

export const PLAN_COLOR_COUNT: number = planIdentity.PLAN_COLOR_COUNT;
const planColorIndex = (plan: { id: string; colorIndex?: number }): number => planIdentity.planColorIndex(plan);

/** The colour slot, kept inside 0 … 7 whatever arrives. */
export function planToneIndex(index: number | undefined): number {
  if (typeof index !== 'number' || !Number.isFinite(index)) return 0;
  const i = Math.trunc(index) % PLAN_COLOR_COUNT;
  return i < 0 ? i + PLAN_COLOR_COUNT : i;
}

/** The CSS variable a slot is drawn with: `var(--plan-tone-3)`. */
export function planToneVar(index: number | undefined): string {
  return `var(--plan-tone-${planToneIndex(index)})`;
}

/** The token's name alone, for `getComputedStyle` (the office canvas cannot read a `var()`). */
export function planToneToken(index: number | undefined): string {
  return `--plan-tone-${planToneIndex(index)}`;
}

/** Spread on an element to put the slot in `--plan` for it and its children (`.plan-dot`, `.plan-chip`, `.plan-edge`). */
export function planToneAttr(index: number | undefined): { 'data-plan-tone': string } {
  return { 'data-plan-tone': String(planToneIndex(index)) };
}

// ── Which slot a plan has, by id ──────────────────────────────────────

const known = new Map<string, number>();
const refs = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

/** Record the slots (and references) of plans the host sent (`plans-state`, `plans-all`). Returns true when anything changed. */
export function rememberPlanColors(plans: ReadonlyArray<{ id?: unknown; colorIndex?: unknown; ref?: unknown }> | undefined): boolean {
  let changed = false;
  for (const p of plans ?? []) {
    if (typeof p?.id !== 'string' || !p.id) continue;
    const slot = planColorIndex({ id: p.id, colorIndex: typeof p.colorIndex === 'number' ? p.colorIndex : undefined });
    if (known.get(p.id) !== slot) {
      known.set(p.id, slot);
      changed = true;
    }
    if (typeof p.ref === 'string' && p.ref && refs.get(p.id) !== p.ref) {
      refs.set(p.id, p.ref);
      changed = true;
    }
  }
  if (changed) {
    version++;
    for (const l of listeners) l();
  }
  return changed;
}

/** A plan's colour slot from its id: the one the host gave it when it was seen, else the stable hash. */
export function planToneOf(planId: string, colorIndex?: number): number {
  if (typeof colorIndex === 'number') return planColorIndex({ id: planId, colorIndex });
  return known.get(planId) ?? planColorIndex({ id: planId });
}

/** A plan's reference (PLAN-2) when a `plans-state` named it. */
export function planRefOf(planId: string): string | undefined {
  return refs.get(planId);
}

/** Changes whenever a slot is learnt, for a hook's dependency list. */
export function planColorsVersion(): number {
  return version;
}

export function subscribePlanColors(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Forget every slot (tests). */
export function resetPlanColors(): void {
  known.clear();
  refs.clear();
  version++;
}

/** Learn slots from host frames as they pass (called once at start-up, App.tsx). */
export function listenForPlanColors(on: (handler: (e: MessageEvent) => void) => () => void): () => void {
  return on((e) => {
    const type = e.data?.type;
    if (type !== 'plans-state' && type !== 'plans-all') return;
    const plans = (e.data.payload as { plans?: Array<{ id?: unknown; colorIndex?: unknown; ref?: unknown }> } | undefined)?.plans;
    if (Array.isArray(plans)) rememberPlanColors(plans);
  });
}
